/**
 * The canvas panel's product analytics (plan section 10, the client half of
 * WP11), sent through `lib/product-analytics` (consent-gated and sanitized,
 * PostHog and the allow-listed GA4 names). Enums only: never an id, a title,
 * a URL or the selected text.
 *
 * - `artifact_panel_edit_opened { kind, from }`: Edit in the panel header,
 *   or Open to review on the suggestions card.
 * - `artifact_selection_action { action, kind }`: a selection toolbar
 *   action (Ask Tabbio, Shorter, Longer, More casual, More formal, Suggest
 *   edits) on a page or a document.
 * - `artifact_version_navigated { direction, kind }`: Previous, Next, a row
 *   of the version list, or Back to latest.
 */
import type { AgentArtifactKind } from '@tabbio/types';
import { trackProductEvent } from '@/lib/product-analytics';

export type EditOpenedFrom = 'header' | 'suggestions';

export type SelectionActionName = 'ask' | 'shorter' | 'longer' | 'casual' | 'formal' | 'suggest';

export type VersionNavigation = 'previous' | 'next' | 'list' | 'latest';

const KINDS: readonly AgentArtifactKind[] = ['document', 'slides', 'page', 'image', 'file'];
const SELECTION_ACTIONS: readonly SelectionActionName[] = ['ask', 'shorter', 'longer', 'casual', 'formal', 'suggest'];
const NAVIGATIONS: readonly VersionNavigation[] = ['previous', 'next', 'list', 'latest'];

/** A kind the event may carry, or null (unknown values are never sent as they are). */
function kindOf(kind: unknown): AgentArtifactKind | null {
  return typeof kind === 'string' && (KINDS as readonly string[]).includes(kind) ? (kind as AgentArtifactKind) : null;
}

export function trackArtifactEditOpened(kind: AgentArtifactKind | null | undefined, from: EditOpenedFrom) {
  trackProductEvent('artifact_panel_edit_opened', { kind: kindOf(kind), from: from === 'suggestions' ? 'suggestions' : 'header' });
}

export function trackArtifactSelectionAction(action: SelectionActionName, selectionKind: 'page' | 'document') {
  if (!SELECTION_ACTIONS.includes(action)) return;
  trackProductEvent('artifact_selection_action', { action, kind: selectionKind === 'page' ? 'page' : 'document' });
}

export function trackArtifactVersionNavigated(direction: VersionNavigation, kind: AgentArtifactKind | null | undefined) {
  if (!NAVIGATIONS.includes(direction)) return;
  trackProductEvent('artifact_version_navigated', { direction, kind: kindOf(kind) });
}
